import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Sparkles, Users, Search, Settings, BarChart3, Building2, Shield, Calendar, FileText, Plus, Upload, LayoutDashboard, Briefcase, Link2, BookOpen, Activity, FileCheck, Clock, GitBranch, MousePointer, ListTodo, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Dashboard steps (shown on /app dashboard)
const DASHBOARD_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to QIndex One',
    description: 'Let\'s take a comprehensive tour of your Client 360 platform. This guide will show you everything you need to know to get started.',
    icon: Sparkles,
    target: null,
    position: 'center'
  },
  {
    id: 'clients',
    title: 'Family Groups Overview',
    description: 'Your client portfolio is organized into Family Groups. Each card displays key metrics including entities, individuals, verified links, and tasks due.',
    icon: Users,
    target: '[data-testid^="client-card-"]',
    position: 'right',
    fallbackTarget: '.caffeine-card'
  },
  {
    id: 'add-client',
    title: 'Adding New Clients',
    description: 'Click "Add Client" to create a new Family Group. You\'ll enter the client name, segment type (Family Office or Advice), assign a relationship lead, and optionally mark as restricted access.',
    icon: Plus,
    target: '[data-testid="add-client-btn"]',
    position: 'bottom'
  },
  {
    id: 'import',
    title: 'Bulk import — beyond just clients',
    description: 'Upload a CSV to create dozens of clients in one go. Same pattern works inside each client for bulk individuals + entities, and on the My Tasks tab for bulk task creation (per-client and org-wide). Every template downloads with valid example rows.',
    icon: Upload,
    target: '[data-testid="bulk-clients-btn"]',
    position: 'bottom',
    fallbackTarget: '[data-testid="add-client-btn"]'
  },
  {
    id: 'quick-filter-chips',
    title: 'Quick filter chips on My Tasks',
    description: 'On the My Tasks tab, one-click chips let you pivot the list to Overdue, Due today, This week, High priority, or Recurring — without re-opening filters every time. Live counts show next to each chip.',
    icon: ListTodo,
    target: '[data-testid="quick-filter-chips"]',
    position: 'bottom',
    fallbackTarget: '[data-testid="add-client-btn"]'
  },
  {
    id: 'search',
    title: 'Enterprise Search',
    description: 'Instantly find any client, individual, entity, adviser, or document across your entire portfolio. Search supports partial matches and filters by type.',
    icon: Search,
    target: '[data-testid="global-search-btn"]',
    position: 'bottom'
  },
  {
    id: 'analytics',
    title: 'Analytics Dashboard',
    description: 'Track portfolio performance with interactive charts showing task completion rates, activity trends, client segments, and team productivity metrics.',
    icon: BarChart3,
    target: '[data-testid="analytics-nav-btn"]',
    position: 'bottom',
    fallbackTarget: '[data-testid="user-profile-btn"]'
  },
  {
    id: 'dark-mode',
    title: 'Theme Preferences',
    description: 'Toggle between light and dark themes for comfortable viewing. Your preference is saved automatically and persists across sessions.',
    icon: Settings,
    target: '[data-testid="dark-mode-toggle"]',
    position: 'bottom'
  },
  {
    id: 'settings',
    title: 'Your Settings',
    description: 'Manage your profile, enable two-factor authentication, customize notifications, and control appearance. Organisation admins can also manage users and access controls here.',
    icon: Settings,
    target: '[data-testid="user-profile-btn"]',
    position: 'left'
  },
  {
    id: 'client-intro',
    title: 'Let\'s Explore a Client Profile',
    description: 'Now let\'s dive into a client profile to see all the powerful features available. Click "Enter Client" to continue the tour inside a real client.',
    icon: MousePointer,
    target: '[data-testid^="client-card-"]',
    position: 'right',
    fallbackTarget: '.caffeine-card',
    action: 'navigate-to-client'
  }
];

// Client profile steps (shown inside /app/clients/:id)
const CLIENT_STEPS = [
  {
    id: 'client-overview',
    title: 'Client Overview',
    description: 'The Overview shows an Action Centre with urgent items, Quick Stats (entities, individuals, links, tasks), and the total group net worth. It\'s your command centre for each client.',
    icon: LayoutDashboard,
    target: '[data-testid="nav-overview"]',
    position: 'right'
  },
  {
    id: 'client-individuals',
    title: 'Individuals',
    description: 'Contains all family members with their roles, contact details, relationships, and important dates. Track the people behind the structures.',
    icon: Users,
    target: '[data-testid="nav-individuals"]',
    position: 'right'
  },
  {
    id: 'client-advisers',
    title: 'Advisers',
    description: 'Lists all external professionals (lawyers, accountants, financial advisers) linked to the family with their contact details and areas of responsibility.',
    icon: Briefcase,
    target: '[data-testid="nav-advisers"]',
    position: 'right'
  },
  {
    id: 'client-entities',
    title: 'Entities & Roles',
    description: 'Shows all legal structures (trusts, companies, SMSFs) with their purpose, directors, beneficiaries, and key dates.',
    icon: Building2,
    target: '[data-testid="nav-entities"]',
    position: 'right'
  },
  {
    id: 'client-matrix',
    title: 'Family/Entity Matrix',
    description: 'A visual diagram showing how all individuals and entities connect. See ownership structures, roles, and relationships at a glance.',
    icon: GitBranch,
    target: '[data-testid="nav-matrix"]',
    position: 'right'
  },
  {
    id: 'client-links',
    title: 'Links & Sources',
    description: 'External documents live here - SharePoint, Google Drive, Dropbox links. Everything is indexed and linked, not stored. Keep your single source of truth.',
    icon: Link2,
    target: '[data-testid="nav-links"]',
    position: 'right'
  },
  {
    id: 'client-workflow',
    title: 'Workflow & Key Dates',
    description: 'Track important deadlines, recurring tasks, and key dates. Never miss a tax deadline, review date, or compliance requirement.',
    icon: Calendar,
    target: '[data-testid="nav-workflow"]',
    position: 'right'
  },
  {
    id: 'meeting-notes-extractor',
    title: 'AI: meeting notes → tasks',
    description: 'On the Workflow tab, paste a paragraph of meeting notes or a call summary. The AI proposes follow-up tasks with title, due date (parsed from natural language like "by Wednesday"), owner, and priority already filled in. You review, edit, and accept — kills the 15-task post-meeting reconstruction.',
    icon: Wand2,
    target: '[data-testid="meeting-extractor-btn"]',
    position: 'bottom',
    fallbackTarget: '[data-testid="nav-workflow"]'
  },
  {
    id: 'client-governance',
    title: 'Governance',
    description: 'Board minutes, resolutions, policies, and decision records. Maintain a complete governance trail for every entity.',
    icon: FileText,
    target: '[data-testid="nav-governance"]',
    position: 'right'
  },
  {
    id: 'client-advice',
    title: 'Advice',
    description: 'Track recommendations and their implementation status. Link advice to entities, individuals, and documents.',
    icon: BookOpen,
    target: '[data-testid="nav-advice"]',
    position: 'right'
  },
  {
    id: 'client-continuity',
    title: 'Continuity Manual',
    description: 'Captures institutional knowledge about the client - wealth history, family purpose, governance structures. Essential for team onboarding and handovers.',
    icon: FileCheck,
    target: '[data-testid="nav-continuity-manual"]',
    position: 'right'
  },
  {
    id: 'client-activity',
    title: 'Activity',
    description: 'Shows recent changes, updates, and communications. See what\'s happening with this client at a glance.',
    icon: Activity,
    target: '[data-testid="nav-activity"]',
    position: 'right'
  },
  {
    id: 'client-audit',
    title: 'Audit Log',
    description: 'A complete, tamper-proof record of every action taken - who did what and when. Full accountability and compliance.',
    icon: Clock,
    target: '[data-testid="nav-audit"]',
    position: 'right'
  },
  {
    id: 'complete',
    title: 'You\'re Ready!',
    description: 'That\'s the full tour! You now know your way around QIndex One. Explore each section to see it in action. You can restart this tour anytime from Settings > Appearance > Product Tour.',
    icon: Sparkles,
    target: null,
    position: 'center'
  }
];

export default function Onboarding({ isOpen, onComplete, onSkip, clients = [] }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [phase, setPhase] = useState('dashboard'); // 'dashboard' or 'client'
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [highlightStyle, setHighlightStyle] = useState({});
  const tooltipRef = useRef(null);
  
  // Refs for keyboard handlers (needed since they're defined after the keyboard effect)
  const handleNextRef = useRef(null);
  const handlePrevRef = useRef(null);

  // Determine which steps to use based on phase
  const steps = phase === 'dashboard' ? DASHBOARD_STEPS : CLIENT_STEPS;
  const step = steps[currentStep];
  const isFirstStep = currentStep === 0 && phase === 'dashboard';
  const isLastStep = phase === 'client' && currentStep === CLIENT_STEPS.length - 1;
  const Icon = step?.icon || Sparkles;

  // Calculate total progress
  const totalSteps = DASHBOARD_STEPS.length + CLIENT_STEPS.length;
  const currentProgress = phase === 'dashboard' 
    ? currentStep + 1 
    : DASHBOARD_STEPS.length + currentStep + 1;

  // Check if we're in a client profile
  useEffect(() => {
    if (!isOpen) return;
    
    const isInClientProfile = location.pathname.includes('/app/clients/') && location.pathname.includes('/overview');
    
    if (isInClientProfile && phase === 'dashboard') {
      // We've navigated to a client, switch to client phase
      setPhase('client');
      setCurrentStep(0);
    }
  }, [location.pathname, isOpen, phase]);

  useEffect(() => {
    if (!isOpen || !step) return;

    const positionTooltip = () => {
      if (step.position === 'center' || !step.target) {
        // Center the tooltip
        setTooltipStyle({
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10001
        });
        setHighlightStyle({});
        return;
      }

      // Find target element
      let targetEl = document.querySelector(step.target);
      if (!targetEl && step.fallbackTarget) {
        targetEl = document.querySelector(step.fallbackTarget);
      }

      if (!targetEl) {
        // Fallback to center if target not found
        setTooltipStyle({
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10001
        });
        setHighlightStyle({});
        return;
      }

      const rect = targetEl.getBoundingClientRect();
      const padding = 8;

      // Set highlight around target
      setHighlightStyle({
        position: 'fixed',
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
        zIndex: 10000
      });

      // Position tooltip based on step.position
      const tooltipWidth = Math.min(360, window.innerWidth - 32);
      const tooltipHeight = 220;
      const margin = 16;
      let top, left;
      let placement = step.position;

      // Mobile: tooltips often don't fit beside the target — force top/bottom
      const isNarrow = window.innerWidth < 720;
      if (isNarrow && (placement === 'left' || placement === 'right')) {
        placement = rect.top > window.innerHeight / 2 ? 'top' : 'bottom';
      }

      switch (placement) {
        case 'right':
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.right + margin;
          // Flip if not enough room on the right
          if (left + tooltipWidth > window.innerWidth - margin) {
            left = rect.left - tooltipWidth - margin;
          }
          break;
        case 'left':
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.left - tooltipWidth - margin;
          if (left < margin) left = rect.right + margin;
          break;
        case 'bottom':
          top = rect.bottom + margin;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
          // Flip to top if no room below
          if (top + tooltipHeight > window.innerHeight - margin) {
            top = rect.top - tooltipHeight - margin;
          }
          break;
        case 'top':
          top = rect.top - tooltipHeight - margin;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
          if (top < margin) top = rect.bottom + margin;
          break;
        default:
          top = rect.bottom + margin;
          left = rect.left;
      }

      // Final clamp to viewport so the Next button is always reachable
      if (left < margin) left = margin;
      if (left + tooltipWidth > window.innerWidth - margin) left = window.innerWidth - tooltipWidth - margin;
      if (top < margin) top = margin;
      if (top + tooltipHeight > window.innerHeight - margin) top = window.innerHeight - tooltipHeight - margin;

      setTooltipStyle({
        position: 'fixed',
        top,
        left,
        width: tooltipWidth,
        zIndex: 10001
      });
    };

    // FIX: scroll the target into view BEFORE positioning. This is the
    // core fix for "Next button below the fold" on mobile — the target was
    // off-screen, so the tooltip pinned next to it landed below the visible
    // area and you couldn't tap Next.
    const scrollAndPosition = () => {
      if (step.target) {
        const targetEl = document.querySelector(step.target) ||
          (step.fallbackTarget && document.querySelector(step.fallbackTarget));
        if (targetEl) {
          // Skip the scroll if the element is already fully visible — avoids
          // jitter on steps that just point at the page header.
          const r = targetEl.getBoundingClientRect();
          const fullyVisible = r.top >= 0 && r.bottom <= window.innerHeight;
          if (!fullyVisible) {
            try {
              targetEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
            } catch {
              targetEl.scrollIntoView();
            }
          }
        }
      }
      // Position after the smooth scroll has had time to land.
      setTimeout(positionTooltip, 360);
    };

    const timer = setTimeout(scrollAndPosition, 100);
    window.addEventListener('resize', positionTooltip);
    window.addEventListener('scroll', positionTooltip);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', positionTooltip);
      window.removeEventListener('scroll', positionTooltip);
    };
  }, [currentStep, isOpen, step, phase]);

  // Keyboard accessibility - Arrow keys and Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          handleNextRef.current?.();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          handlePrevRef.current?.();
          break;
        case 'Escape':
          event.preventDefault();
          onSkip();
          break;
        case 'Enter':
        case ' ':
          // If focused on a button, let it handle the event
          if (document.activeElement?.tagName === 'BUTTON') {
            return;
          }
          event.preventDefault();
          handleNextRef.current?.();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onSkip]);

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
      return;
    }

    // Check if this step has a navigation action
    if (step?.action === 'navigate-to-client') {
      // Find the first client and navigate to it
      const firstClientCard = document.querySelector('[data-testid^="client-card-"]');
      if (firstClientCard) {
        const testId = firstClientCard.getAttribute('data-testid');
        const clientId = testId.replace('client-card-', '');
        navigate(`/app/clients/${clientId}/overview`);
        // Phase will switch via useEffect when location changes
      } else if (clients.length > 0) {
        navigate(`/app/clients/${clients[0].id}/overview`);
      }
      return;
    }

    // Normal next step
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else if (phase === 'dashboard') {
      // Move to client phase
      setPhase('client');
      setCurrentStep(0);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    } else if (phase === 'client') {
      // Go back to dashboard
      setPhase('dashboard');
      setCurrentStep(DASHBOARD_STEPS.length - 1);
      navigate('/app');
    }
  };

  const handleSkip = () => {
    onSkip();
  };

  // Update refs for keyboard handlers
  useEffect(() => {
    handleNextRef.current = handleNext;
    handlePrevRef.current = handlePrev;
  });

  if (!isOpen || !step) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity duration-300"
        style={{ zIndex: 9999 }}
        onClick={handleSkip}
        data-testid="onboarding-overlay"
      />

      {/* Highlight box around target */}
      {highlightStyle.width && (
        <div
          className="rounded-lg ring-4 ring-[#4A3B32] ring-opacity-50 pointer-events-none"
          style={{
            ...highlightStyle,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
            background: 'transparent'
          }}
          data-testid="onboarding-highlight"
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="w-[360px] bg-white rounded-xl shadow-2xl overflow-hidden animate-fade-in"
        style={tooltipStyle}
        data-testid="onboarding-tooltip"
      >
        {/* Header */}
        <div className="bg-[#4A3B32] text-white px-5 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Icon className="w-5 h-5" />
              <span className="text-xs text-white/70">
                Step {currentProgress} of {totalSteps}
                {phase === 'client' && ' • Client Profile'}
              </span>
            </div>
            <button
              onClick={handleSkip}
              className="text-white/70 hover:text-white transition-colors"
              data-testid="onboarding-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <h3 className="font-heading text-lg">{step.title}</h3>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          <p className="text-[#5D524D] text-sm leading-relaxed mb-4">
            {step.description}
          </p>

          {/* Progress dots */}
          <div className="flex justify-center gap-1.5 mb-4">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-all ${
                  i + 1 === currentProgress
                    ? 'bg-[#4A3B32] w-4'
                    : i + 1 < currentProgress
                    ? 'bg-[#4A3B32]/60'
                    : 'bg-[#E6E0D9]'
                }`}
              />
            ))}
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrev}
              disabled={isFirstStep}
              className="text-[#5D524D] hover:text-[#2C2420]"
              data-testid="onboarding-prev-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              className="text-[#8C8078] hover:text-[#5D524D]"
              data-testid="onboarding-skip-btn"
            >
              Skip Tour
            </Button>

            <Button
              size="sm"
              onClick={handleNext}
              className="bg-[#4A3B32] hover:bg-[#3A2E26] text-white"
              data-testid="onboarding-next-btn"
            >
              {isLastStep ? 'Finish' : step.action === 'navigate-to-client' ? 'Enter Client' : 'Next'}
              {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>

          {/* Keyboard shortcuts hint */}
          <div className="mt-3 pt-3 border-t border-[#E6E0D9] text-center">
            <p className="text-xs text-[#8C8078]">
              <span className="inline-flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 text-[10px] bg-[#F5F3F0] rounded border border-[#E6E0D9] font-mono">←</kbd>
                <kbd className="px-1.5 py-0.5 text-[10px] bg-[#F5F3F0] rounded border border-[#E6E0D9] font-mono">→</kbd>
                <span className="mx-1">Navigate</span>
              </span>
              <span className="mx-2">·</span>
              <span className="inline-flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 text-[10px] bg-[#F5F3F0] rounded border border-[#E6E0D9] font-mono">Esc</kbd>
                <span className="ml-1">Skip</span>
              </span>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
